import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n';
import PasswordStrength, { checkPassword } from '../components/shared/PasswordStrength';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import api from '../utils/api';
import versionInfo from '../version.json';

const EyeIcon = ({ off }) => off ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const field = {
  width: '100%', padding: '11px 14px', border: '1.5px solid var(--border)',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none',
  background: 'var(--input-bg)', color: 'var(--text)', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

// Renders auth.agree ('… {terms} and {privacy}.') with the placeholders
// swapped for links, so the sentence stays translatable as one string.
function AgreeNotice({ t }) {
  const linkStyle = { color: 'var(--blue)', fontWeight: 600, textDecoration: 'none' };
  const parts = t('auth.agree').split(/(\{terms\}|\{privacy\})/);
  return (
    <p style={{ fontSize: 12, color: 'var(--text-subtle)', lineHeight: 1.6, textAlign: 'center', marginTop: 2 }}>
      {parts.map((part, i) => {
        if (part === '{terms}')   return <Link key={i} to="/terms"   target="_blank" style={linkStyle}>{t('footer.terms')}</Link>;
        if (part === '{privacy}') return <Link key={i} to="/privacy" target="_blank" style={linkStyle}>{t('footer.privacy')}</Link>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </p>
  );
}

function Field({ label, id, error, children }) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</label>
      {children}
      {error && <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{error}</p>}
    </div>
  );
}

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode]         = useState(params.get('mode') === 'register' ? 'register' : 'login');
  const [view, setView]         = useState(params.get('view') === 'forgot' ? 'forgot' : 'main'); // 'main' | 'forgot'
  const [form, setForm]         = useState({ name: '', email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent]   = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');
  const [twofa, setTwofa]   = useState(null); // { method, challengeToken }
  const [twofaCode, setTwofaCode]   = useState('');
  const [twofaError, setTwofaError] = useState('');
  const [twofaBusy, setTwofaBusy]   = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [resent, setResent] = useState(false);
  const { login, register, completeTwoFactor, resendTwoFactorCode } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t, isRTL } = useT();
  const navigate = useNavigate();

  // Load remembered email on mount
  useEffect(() => {
    const saved = localStorage.getItem('rememberedEmail');
    if (saved) { setForm(f => ({ ...f, email: saved })); setRemember(true); }
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const switchMode = m => { setMode(m); setError(''); setForm({ name: '', email: form.email, password: '' }); };

  const submit = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (mode === 'register' && !checkPassword(form.password).valid) {
        throw new Error(t('auth.weakPassword'));
      }
      if (mode === 'login') {
        const result = await login(form.email, form.password);
        if (result?.twoFactorRequired) {
          setTwofa({ method: result.method, challengeToken: result.challengeToken });
          setTwofaCode(''); setTwofaError(''); setResent(false);
          setView('twofa');
          return;
        }
      }
      else await register(form.name, form.email, form.password);
      if (remember) localStorage.setItem('rememberedEmail', form.email);
      else localStorage.removeItem('rememberedEmail');
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('auth.invalidCredentials'));
    } finally { setLoading(false); }
  };

  const submitForgot = async e => {
    e.preventDefault();
    setForgotError(''); setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.response?.data?.message || t('auth.resetSendFailed'));
    } finally { setForgotLoading(false); }
  };

  const leaveForgot = () => { setView('main'); setForgotSent(false); setForgotEmail(''); setForgotError(''); };

  const submitTwofa = async e => {
    e.preventDefault();
    setTwofaError(''); setTwofaBusy(true);
    try {
      await completeTwoFactor(twofa.challengeToken, twofaCode.trim(), trustDevice);
      if (remember) localStorage.setItem('rememberedEmail', form.email);
      else localStorage.removeItem('rememberedEmail');
      navigate('/');
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'challengeExpired') {
        setView('main'); setTwofa(null);
        setError(err.response?.data?.message || t('auth.invalidCredentials'));
      } else {
        setTwofaError(err.response?.data?.message || t('auth.invalidCredentials'));
      }
    } finally { setTwofaBusy(false); }
  };

  const resendCode = async () => {
    try {
      await resendTwoFactorCode(twofa.challengeToken);
      setResent(true);
      setTimeout(() => setResent(false), 60000);
    } catch (err) {
      setTwofaError(err.response?.data?.message || t('auth.invalidCredentials'));
    }
  };

  const inputStyle = k => ({
    ...field,
    borderColor: focused === k ? 'var(--blue)' : error ? 'rgba(239,68,68,0.5)' : 'var(--border)',
    boxShadow: focused === k ? '0 0 0 3px rgba(46,117,182,0.12)' : 'none',
  });

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', position: 'relative',
      background: 'linear-gradient(135deg, #0f2447 0%, #1A3C6E 50%, #2E75B6 100%)',
      alignItems: 'center', justifyContent: 'center', padding: 16,
      fontFamily: "'Segoe UI', Arial, sans-serif",
    }}>
      {/* Top-right toolbar — Home / Language / Theme */}
      <div style={{
        position: 'fixed', top: 16, insetInlineEnd: 16, zIndex: 50,
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <Link to="/" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
          color: '#fff', padding: '7px 14px', borderRadius: 8,
          fontSize: 13, fontWeight: 600, textDecoration: 'none',
        }} title={t('nav.home')}>
          <span>{isRTL ? '→' : '←'}</span>
          <span>{t('nav.home')}</span>
        </Link>
        <LanguageSwitcher variant="dark" compact />
        <button onClick={toggleTheme} aria-label="Toggle theme" title={theme === 'dark' ? t('nav.light') : t('nav.dark')}
          style={{
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
            color: '#fff', cursor: 'pointer', padding: '7px 11px', borderRadius: 8,
            fontSize: 14, fontFamily: 'inherit',
          }}>
          {theme === 'dark' ? '☀' : '🌙'}
        </button>
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', width: '100%', maxWidth: 860,
        borderRadius: 20, overflow: 'hidden',
        boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
      }}>

        {/* Left panel — branding */}
        <div style={{
          flex: '1 1 300px', minWidth: 0, background: 'linear-gradient(160deg, #1A3C6E 0%, #0f2447 100%)',
          padding: '52px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Decorative circles */}
          <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(46,117,182,0.18)' }} />
          <div style={{ position: 'absolute', bottom: -80, left: -40, width: 260, height: 260, borderRadius: '50%', background: 'rgba(46,117,182,0.12)' }} />

          <div style={{ position: 'relative' }}>
            {/* Logo wordmark */}
            <div style={{ marginBottom: 20 }}>
              <span style={{ fontSize: 42, fontWeight: 900, color: '#F59E0B', letterSpacing: '-2px', lineHeight: 1 }}>I</span>
              <span style={{ fontSize: 42, fontWeight: 700, color: '#ffffff', letterSpacing: '-1.5px', lineHeight: 1 }}>nvio</span>
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
              {t('auth.tagline')}
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 8 }}>invio.my · v{versionInfo.version}</p>
          </div>

          <div style={{ position: 'relative' }}>
            {[
              { icon: '📄', text: t('auth.feature1') },
              { icon: '📊', text: t('auth.feature2') },
              { icon: '🖨', text: t('auth.feature3') },
            ].map(f => (
              <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.1)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{f.icon}</div>
                <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.8)' }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right panel — form */}
        <div style={{ flex: '1 1 320px', minWidth: 0, background: 'var(--card-bg)', padding: '52px 44px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

          {/* ── 2FA CODE VIEW ── */}
          {view === 'twofa' ? (
            <div>
              <button onClick={() => { setView('main'); setTwofa(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, marginBottom: 28, display: 'flex', alignItems: 'center', gap: 6, padding: 0, fontFamily: 'inherit' }}>
                {isRTL ? '→' : '←'} {t('auth.backToSignIn')}
              </button>
              <div style={{ fontSize: 34, marginBottom: 10 }}>🔐</div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)', marginBottom: 8 }}>{t('twofa.title')}</h2>
              <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 22 }}>
                {twofa?.method === 'email' ? t('twofa.enterEmailCode') : t('twofa.enterAppCode')}
              </p>
              <form onSubmit={submitTwofa} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <input
                  autoFocus value={twofaCode} onChange={e => setTwofaCode(e.target.value.replace(/[^\d\s]/g, ''))}
                  inputMode="numeric" autoComplete="one-time-code" placeholder="000000" dir="ltr"
                  style={{
                    ...inputStyle('twofa'), textAlign: 'center', fontSize: 26, letterSpacing: 12,
                    fontFamily: "'Courier New', monospace", fontWeight: 700, padding: '14px 8px',
                  }}
                  onFocus={() => setFocused('twofa')} onBlur={() => setFocused('')} />
                {twofaError && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16 }}>⚠️</span>
                    <span style={{ fontSize: 13, color: 'var(--danger)' }}>{twofaError}</span>
                  </div>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)}
                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--navy)' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('twofa.trustDevice')}</span>
                </label>
                <button type="submit" disabled={twofaBusy || twofaCode.trim().length < 6}
                  style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: twofaBusy ? 0.75 : 1 }}>
                  {twofaBusy ? t('auth.pleaseWait') : t('twofa.verify')}
                </button>
                {twofa?.method === 'email' && (
                  <button type="button" onClick={resendCode} disabled={resent}
                    style={{ background: 'none', border: 'none', cursor: resent ? 'default' : 'pointer', color: resent ? 'var(--text-subtle)' : 'var(--blue)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', padding: 0 }}>
                    {resent ? t('twofa.codeSent') : t('twofa.resend')}
                  </button>
                )}
              </form>
            </div>

          ) : view === 'forgot' ? (
            <div>
              <button onClick={leaveForgot}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, marginBottom: 28, display: 'flex', alignItems: 'center', gap: 6, padding: 0, fontFamily: 'inherit' }}>
                {isRTL ? '→' : '←'} {t('auth.backToSignIn')}
              </button>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)', marginBottom: 8 }}>{t('auth.resetTitle')}</h2>
              {forgotSent ? (
                <div>
                  <div style={{ background: 'rgba(16,185,129,0.1)', border: '1.5px solid rgba(16,185,129,0.4)', borderRadius: 10, padding: '16px 18px', marginBottom: 14 }}>
                    <p style={{ fontSize: 14, color: 'var(--success)', fontWeight: 600, marginBottom: 4 }}>{t('auth.checkInbox')}</p>
                    <p style={{ fontSize: 13, color: 'var(--success)' }}>{t('auth.ifRegistered', { email: forgotEmail })}</p>
                  </div>
                  <p style={{ fontSize: 12.5, color: 'var(--text-subtle)', lineHeight: 1.6, marginBottom: 20 }}>{t('auth.resetSpamHint')}</p>
                  <button onClick={leaveForgot}
                    style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>
                    {t('auth.backToSignInBtn')}
                  </button>
                </div>
              ) : (
                <form onSubmit={submitForgot} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 4 }}>
                    {t('auth.resetIntro')}
                  </p>
                  <Field label={t('auth.email')} id="forgot-email">
                    <input id="forgot-email" type="email" required value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      style={{ ...inputStyle('forgot'), width: '100%', boxSizing: 'border-box' }}
                      onFocus={() => setFocused('forgot')} onBlur={() => setFocused('')} />
                  </Field>
                  {forgotError && (
                    <div style={{ background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 16 }}>⚠️</span>
                      <span style={{ fontSize: 13, color: 'var(--danger)' }}>{forgotError}</span>
                    </div>
                  )}
                  <button type="submit" disabled={forgotLoading} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: forgotLoading ? 0.75 : 1 }}>
                    {forgotLoading ? t('auth.pleaseWait') : t('auth.sendResetLink')}
                  </button>
                </form>
              )}
            </div>

          ) : (
          /* ── MAIN LOGIN / REGISTER VIEW ── */
          <div>
            {/* Tab switcher */}
            <div style={{ display: 'flex', background: 'var(--hover-bg)', borderRadius: 10, padding: 4, marginBottom: 32 }}>
              {['login', 'register'].map(m => (
                <button key={m} type="button" onClick={() => switchMode(m)}
                  style={{
                    flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, transition: 'all 0.15s',
                    background: mode === m ? 'var(--card-bg)' : 'transparent',
                    color: mode === m ? 'var(--heading)' : 'var(--text-muted)',
                    boxShadow: mode === m ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  }}>
                  {m === 'login' ? t('auth.signInTab') : t('auth.registerTab')}
                </button>
              ))}
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
              {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccountTitle')}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-subtle)', marginBottom: 28 }}>
              {mode === 'login' ? t('auth.signInSub') : t('auth.registerSub')}
            </p>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {mode === 'register' && (
                <Field label={t('auth.fullName')} id="name">
                  <input id="name" type="text" required value={form.name} onChange={set('name')}
                    placeholder={t('auth.fullNamePlaceholder')}
                    style={inputStyle('name')}
                    onFocus={() => setFocused('name')} onBlur={() => setFocused('')} />
                </Field>
              )}

              <Field label={t('auth.email')} id="email">
                <input id="email" type="email" required value={form.email} onChange={set('email')}
                  placeholder={t('auth.emailPlaceholder')}
                  style={inputStyle('email')}
                  onFocus={() => setFocused('email')} onBlur={() => setFocused('')} />
              </Field>

              <Field label={t('auth.password')} id="password">
                <div style={{ position: 'relative' }}>
                  <input id="password" type={showPass ? 'text' : 'password'} required
                    value={form.password} onChange={set('password')}
                    placeholder={t('auth.passwordPlaceholder')}
                    style={{ ...inputStyle('password'), paddingInlineEnd: 44 }}
                    onFocus={() => setFocused('password')} onBlur={() => setFocused('')} />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    style={{
                      position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)',
                      display: 'flex', alignItems: 'center', padding: 2,
                    }}
                    title={showPass ? t('auth.hidePassword') : t('auth.showPassword')}>
                    <EyeIcon off={showPass} />
                  </button>
                </div>
                {mode === 'register' && <PasswordStrength password={form.password} />}
              </Field>

              {mode === 'login' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -6, gap: 8, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                      style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--navy)' }} />
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('auth.rememberMe')}</span>
                  </label>
                  <button type="button" onClick={() => { setView('forgot'); setForgotEmail(form.email); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', padding: 0 }}>
                    {t('auth.forgotPassword')}
                  </button>
                </div>
              )}

              {error && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <span style={{ fontSize: 13, color: 'var(--danger)' }}>{error}</span>
                </div>
              )}

              <button type="submit" disabled={loading} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', marginTop: 4, opacity: loading ? 0.75 : 1 }}>
                {loading
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                      {t('auth.pleaseWait')}
                    </span>
                  : mode === 'login' ? t('auth.signInBtn') : t('auth.createAccountBtn')
                }
              </button>

              {mode === 'register' && <AgreeNotice t={t} />}
            </form>
          </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const btnPrimary = {
  display: 'flex', alignItems: 'center', padding: '12px 24px',
  background: 'linear-gradient(135deg, #1A3C6E, #2E75B6)',
  color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.3px',
  boxShadow: '0 4px 14px rgba(26,60,110,0.35)', transition: 'opacity 0.15s',
};
